import React, { useEffect } from 'react';
import usePayments from '../../../../../hooks/api/usePayments';
import { getIntl } from '../../../../../utils/intl';
import { useResetApp } from '../../../../ResetAppProvider';
import useUser from '../../../../../hooks/api/useUser';
import { Text as RNText, View } from 'react-native';
import { entitlements } from '../../../../../utils/sharedMessages';
import getDateString from '../../../../../utils/getDateString';
import { isPresent } from 'ts-is-present';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import DetailsLayout from '../../../../v2BaseComponents/DetailsLayout';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { getSpacing } from '../../../../../utils/spacing';
import Icon from '../../../../v2BaseComponents/Icon';
import ListItem from '../../../../v2BaseComponents/ListItem';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';

const intl = getIntl('landingScreen-stripProducts', {
    noProducts: 'You currently have not subscribed to or purchased any plans.',
    myProducts: 'My Purchases',
    continue: 'Continue',
    yourNextPayment: 'Your next payment will be {date}',
    unlockedFeatures: "You've unlocked the premium features",
    organize6Profiles: 'Organize up to 6 Medical Profiles',
    organize1Profile: 'Organize 1 Medical Profile',
    shareAccess: 'Sharing access with as many care team members as you choose',
    thankYou: 'Thank You, {name}',
    planNowActive: '{planName} is now active',
    setupAndTraining: 'Received 2 hours of training & setup a $299 value',
});

export type StripeProductsOwnProps = {
    onContinue: () => void;
};

export type StripeProductsProps = StripeProductsOwnProps;

const StripeProducts: React.FC<StripeProductsProps> = ({ onContinue }) => {
    const resetApp = useResetApp();
    const { user } = useUser();
    const {
        isLoadingPaymentStatus: isLoadingMyEntitlement,
        resetPaymentStatus: resetMyEntitlement,
        paymentStatus,
        myMostPermissiveEntitlement,
    } = usePayments({ pollForPaymentStatus: true });
    useEffect(() => {
        resetMyEntitlement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isLoadingMyEntitlement && !myMostPermissiveEntitlement) {
            // no products, nothing to display.  reset app
            resetApp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingMyEntitlement, myMostPermissiveEntitlement]);

    return (
        <AnimateLoadedContent isLoading={isLoadingMyEntitlement || !myMostPermissiveEntitlement || user === 'loading'}>
            {() =>
                isLoadingMyEntitlement || !myMostPermissiveEntitlement || user === 'loading' ? null : (
                    <ActionButtonLayout
                        actions={[
                            {
                                icon: 'CheckCircle',
                                onPress: onContinue,
                                title: intl('continue'),
                                testID: 'stripe-products-continue-button',
                            },
                        ]}
                    >
                        <DetailsLayout>
                            <MainContentContainer>
                                <Stack
                                    vertical
                                    fullWidth
                                    fullHeight
                                    align="center"
                                    justify="center"
                                    space={contentStackSpacing}
                                >
                                    <Stack vertical justify={'center'} align={'center'} space={0}>
                                        <RNText style={{ fontSize: 60, paddingLeft: 20 }}>{'🎉'}</RNText>
                                        <Text variant="h2" alignText="center">
                                            {intl('thankYou', { name: user.preferredName || user.firstName || '' })}
                                        </Text>
                                    </Stack>
                                    <Stack
                                        horizontal
                                        space={getSpacing(0.25)}
                                        align="flex-start"
                                        style={{ maxWidth: '80%' }}
                                    >
                                        <Icon name="Check" colorVariant="Success" size="avatar-icon" />
                                        <Stack vertical justify={'center'} space={0}>
                                            <Text variant="h5">
                                                {intl('planNowActive', {
                                                    planName: entitlements(myMostPermissiveEntitlement),
                                                })}
                                            </Text>
                                            {paymentStatus ? (
                                                <Text variant="body-1">
                                                    {intl('yourNextPayment', {
                                                        date: getDateString(paymentStatus.nextPaymentTimestamp, 'date'),
                                                    })}
                                                </Text>
                                            ) : null}
                                        </Stack>
                                        <View style={{ opacity: 0 }}>
                                            <Icon name="Check" colorVariant="Success" size="avatar-icon" />
                                        </View>
                                    </Stack>
                                    <Stack
                                        vertical
                                        justify={'center'}
                                        align={'flex-start'}
                                        space={0}
                                        style={{ maxWidth: 300 }}
                                    >
                                        <Text variant="body-1" alignText="center">
                                            {intl('unlockedFeatures')}
                                        </Text>
                                        {(
                                            [
                                                'organize6Profiles',
                                                'shareAccess',
                                                // @ts-expect-error TS18047
                                                paymentStatus.entitlements.includes('Setup and Training')
                                                    ? 'setupAndTraining'
                                                    : null,
                                            ] as const
                                        )
                                            .filter(isPresent)
                                            .map((_) => (
                                                <ListItem unordered key={_} variant="body-1">
                                                    {intl(_)}
                                                </ListItem>
                                            ))}
                                    </Stack>
                                </Stack>
                            </MainContentContainer>
                        </DetailsLayout>
                    </ActionButtonLayout>
                )
            }
        </AnimateLoadedContent>
    );
};

export default React.memo(StripeProducts);
